/**
 * Casca da aplicação.
 *
 * A navegação é uma barra lateral fixa à esquerda, fechada por padrão: no
 * lugar dos dois botões flutuantes que existiam (casa à esquerda, três
 * pontinhos à direita), um lugar só, com todas as telas visíveis de uma vez e
 * a tela atual destacada. Fechada ela ocupa 4 rem, o que devolve a largura
 * inteira para o conteúdo — que é o que o quadro kanban precisa.
 *
 * O menu deixa de ser um dropdown copiado em cada arquivo HTML, com links
 * absolutos para `http://192.168.0.173/...` que impediam trocar o host (#28);
 * agora são rotas reais, com o botão voltar do navegador funcionando e URL
 * própria por tela.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { secoesEfetivas, type CodigoSecao } from '@tcm/shared';
import { cx, Faixa } from './ui';
import { ApiError } from '../lib/api';
import { useAquecerCatalogos, useSair, useSessao } from '../features/queries';

/**
 * O menu inteiro, na ordem em que aparece.
 *
 * `secao` amarra cada item ao catálogo de `@tcm/shared`: é a mesma lista que a
 * tela Admin → Usuários mostra em caixinhas, então marcar "Caixa" lá e o item
 * "Caixa" sumir daqui não dependem de ninguém lembrar de atualizar os dois
 * lugares — o rótulo é o daqui, o direito de ver é o de lá.
 */
const MENU = [
  { para: '/', secao: 'monitor', rotulo: 'Monitor de OS', icone: IconeMonitor },
  { para: '/os/nova', secao: 'os_nova', rotulo: 'Gerar OS', icone: IconeNovaOs },
  { para: '/orcamento', secao: 'orcamento', rotulo: 'Orçamento', icone: IconeOrcamento },
  { para: '/clientes', secao: 'clientes', rotulo: 'Base de Clientes', icone: IconeClientes },
  { para: '/caixa', secao: 'caixa', rotulo: 'Caixa', icone: IconeCaixa },
  { para: '/caixa/novo', secao: 'caixa_lancamento', rotulo: 'Lançamento', icone: IconeLancamento },
  { para: '/mensagens', secao: 'mensagens', rotulo: 'Mensagens', icone: IconeMensagens },
  { para: '/relatorios/dashboard', secao: 'dashboard', rotulo: 'Dashboard', icone: IconeDashboard },
  { para: '/relatorios/os-zeradas', secao: 'os_zeradas', rotulo: 'OS Zeradas', icone: IconeZeradas },
  { para: '/admin', secao: 'admin', rotulo: 'Admin', icone: IconeAdmin },
] as const satisfies ReadonlyArray<{
  para: string;
  secao: CodigoSecao;
  rotulo: string;
  icone: () => ReactNode;
}>;

/**
 * Largura da barra fechada — é ela que o conteúdo compensa com `lg:pl-16`.
 *
 * Escrita por extenso, e não montada com template string: o Tailwind gera CSS
 * a partir do texto do fonte, então uma classe que só existe depois de
 * interpolada (`lg:${TRILHO}`) nunca chega à folha de estilo.
 */
const TRILHO_FECHADO = 'lg:w-16';

export function AppShell({
  children,
  escuro = false,
  creme = false,
}: {
  children: ReactNode;
  escuro?: boolean;
  /**
   * Fundo quente do quadro kanban. O `slate-100` das demais telas é um cinza
   * frio: ao lado das colunas creme ele puxa para o azulado e desmancha a
   * paleta. `escuro` continua tendo prioridade — é a parede da oficina.
   */
  creme?: boolean;
}) {
  const [gaveta, setGaveta] = useState(false);
  // Estável: a barra lateral usa esta função como dependência de efeito, e uma
  // arrow nova a cada render faria os efeitos religarem sem motivo.
  const fecharGaveta = useCallback(() => setGaveta(false), []);

  // Aqui e não em cada tela: a casca só existe para quem já entrou, e o
  // aquecimento vale para todas as telas que a usam.
  useAquecerCatalogos();

  return (
    <div
      className={cx(
        // `min-h-dvh` e não `min-h-screen`: no celular o `100vh` conta a barra
        // de endereço que se esconde ao rolar, e a tela ficava sempre um
        // pouco mais alta que o visível — rodapé cortado, rolagem fantasma.
        'min-h-dvh',
        escuro ? 'bg-slate-900 text-slate-100' : creme ? 'bg-creme-100 text-stone-800' : 'bg-slate-100',
      )}
    >
      {/* No celular o trilho de 4 rem comia 17% da largura de um iPhone e só
          abria no `hover`, que o toque não tem. Abaixo de `lg` ele dá lugar a
          uma barra no topo com o botão do menu, e a navegação vira gaveta. */}
      <BarraSuperior escuro={escuro} aoAbrir={() => setGaveta(true)} />
      <BarraLateral escuro={escuro} gaveta={gaveta} aoFecharGaveta={fecharGaveta} />

      {/* O recuo é o do trilho fechado, e só existe onde o trilho existe. A
          barra aberta passa POR CIMA do conteúdo: se empurrasse, o quadro
          kanban reflowaria a cada abertura. */}
      <div className="lg:pl-16">
        <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-10 lg:px-8">{children}</main>
      </div>
    </div>
  );
}

/**
 * Cabeçalho do celular — existe só abaixo de `lg`.
 *
 * Fica grudado no topo porque as telas de trabalho são longas (o quadro, a
 * lista de clientes, o caixa) e voltar ao menu não pode custar rolar a página
 * inteira de volta.
 */
function BarraSuperior({ escuro, aoAbrir }: { escuro: boolean; aoAbrir: () => void }) {
  return (
    <header
      className={cx(
        'sticky top-0 z-30 flex items-center gap-3 border-b px-2 area-segura-topo lg:hidden',
        // Semitransparente com desfoque: o conteúdo continua sendo percebido
        // rolando por baixo, em vez de sumir atrás de uma faixa opaca.
        escuro
          ? 'border-slate-800 bg-slate-900/85 backdrop-blur'
          : 'border-creme-200 bg-creme-50/85 backdrop-blur',
      )}
    >
      <button
        type="button"
        onClick={aoAbrir}
        aria-label="Abrir menu"
        className={cx(
          'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl transition-colors',
          escuro ? 'active:bg-slate-800' : 'active:bg-creme-200',
        )}
      >
        <svg {...TRACO} className="h-6 w-6">
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      </button>

      <img src="/ico.png" alt="" aria-hidden="true" className="h-7 w-7 rounded-lg object-contain" />
      <span className={cx('text-sm font-bold', escuro ? 'text-slate-100' : 'text-stone-800')}>
        Sistema TCM
      </span>
    </header>
  );
}

/**
 * Navegação principal — trilho fixo à esquerda, fechado por padrão.
 *
 * Substitui os dois botões flutuantes (casa à esquerda, três pontinhos à
 * direita) por um lugar só. Fechada, a barra mostra os ícones e cabe em 4 rem;
 * o rótulo aparece no `title` para quem passa o mouse e no leitor de tela.
 * Abre no clique do botão ou quando o ponteiro entra nela, e fecha ao navegar,
 * no Esc ou ao sair — nunca fica aberta atrapalhando a tela de trabalho.
 */
function BarraLateral({
  escuro,
  gaveta,
  aoFecharGaveta,
}: {
  escuro: boolean;
  /** Gaveta aberta pelo botão da barra superior — só existe abaixo de `lg`. */
  gaveta: boolean;
  aoFecharGaveta: () => void;
}) {
  const rota = useRouterState({ select: (s) => s.location.pathname });
  const sessao = useSessao();
  const [fixada, setFixada] = useState(false);
  const [sobre, setSobre] = useState(false);

  /**
   * O menu daquela conta.
   *
   * A lista vem RESOLVIDA de `/auth/me` — o backend já trocou "sem escolha" pelo
   * padrão do papel. Enquanto a sessão não chegou, o menu fica vazio em vez de
   * completo: piscar o item "Admin" para quem não pode vê-lo é pior do que
   * piscar nada.
   *
   * Com a sessão em mãos, porém, o campo passa por `secoesEfetivas`: se a
   * resposta vier sem `secoes` (API antiga, ou o fallback do `/auth/me` com o
   * banco fora do ar), o padrão do papel entra no lugar. Menu vazio para quem
   * ESTÁ logado não é "sem permissão", é sistema sem navegação.
   */
  const usuario = sessao.data?.usuario;
  const itens = useMemo(() => {
    if (!usuario) return [];
    const permitidas = new Set<CodigoSecao>(secoesEfetivas(usuario.papel, usuario.secoes));
    return MENU.filter((item) => permitidas.has(item.secao));
  }, [usuario]);
  // No celular quem manda é a gaveta; `fixada`/`sobre` são gestos de mouse.
  const aberta = fixada || sobre || gaveta;

  useEffect(() => {
    if (!fixada && !gaveta) return;
    const aoTeclar = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      setFixada(false);
      aoFecharGaveta();
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [fixada, gaveta, aoFecharGaveta]);

  // Gaveta aberta trava a rolagem do conteúdo atrás — sem isto o quadro rola
  // por baixo do menu enquanto o dedo tenta escolher a tela.
  useEffect(() => {
    if (!gaveta) return;
    const antes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = antes;
    };
  }, [gaveta]);

  /**
   * Passou para a largura de desktop com a gaveta aberta (tablet girado, janela
   * esticada): o véu some com o `lg:hidden`, mas `gaveta` continuaria valendo e
   * a barra ficaria presa aberta — `onPointerLeave` só apaga o `sobre`.
   */
  useEffect(() => {
    if (!gaveta) return;
    const consulta = window.matchMedia('(min-width: 1024px)');
    if (consulta.matches) {
      aoFecharGaveta();
      return;
    }
    consulta.addEventListener('change', aoFecharGaveta);
    return () => consulta.removeEventListener('change', aoFecharGaveta);
  }, [gaveta, aoFecharGaveta]);

  // Trocou de tela: a barra sai da frente sozinha.
  useEffect(() => {
    setFixada(false);
    setSobre(false);
    aoFecharGaveta();
  }, [rota, aoFecharGaveta]);

  /**
   * Item destacado: o de caminho mais longo que casa com a rota.
   *
   * Prefixo simples não serve — em `/caixa/novo` tanto "Caixa" quanto
   * "Lançamento" casariam, e a barra acenderia duas linhas.
   */
  const destaque = itens
    .map((m) => m.para as string)
    .filter((para) => (para === '/' ? rota === '/' : rota === para || rota.startsWith(`${para}/`)))
    .sort((a, b) => b.length - a.length)[0];

  return (
    <>
      {/* Véu da gaveta: escurece a tela de trabalho e recebe o toque fora do
          menu, que é como se fecha uma gaveta no celular. */}
      <div
        onClick={aoFecharGaveta}
        aria-hidden="true"
        className={cx(
          'fixed inset-0 z-30 bg-stone-900/40 backdrop-blur-sm transition-opacity duration-200 lg:hidden',
          gaveta ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      />

      <nav
        aria-label="Navegação principal"
        onPointerEnter={(e) => {
          if (e.pointerType === 'mouse') setSobre(true);
        }}
        onPointerLeave={() => setSobre(false)}
        className={cx(
          'fixed inset-y-0 left-0 z-40 flex flex-col overflow-hidden border-r area-segura-topo area-segura-base',
          'transition-[width,transform] duration-200',
          // Celular: largura fixa de gaveta, escondida fora da tela até abrir.
          // O trilho de ícones não existe aqui — sem `hover`, ele seria uma
          // fileira de símbolos sem rótulo ocupando um sexto da largura.
          //
          // Sem `lg:w-auto` aqui: as três classes de largura do `lg` têm a
          // mesma especificidade, e o Tailwind emite `w-auto` DEPOIS das
          // numéricas — então ela vencia `lg:w-16`/`lg:w-60` e a barra ficava
          // presa na largura do conteúdo, sem fechar nem abrir. Quem manda no
          // desktop são as duas linhas abaixo; esta só serve à gaveta.
          'w-72',
          gaveta ? 'translate-x-0 shadow-2xl' : '-translate-x-full lg:translate-x-0',
          // Desktop: volta a ser o trilho que cresce.
          aberta ? 'lg:w-60 lg:shadow-xl' : TRILHO_FECHADO,
          escuro ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white',
        )}
      >
        <div className="flex h-16 shrink-0 items-center gap-3 px-3">
          <button
            type="button"
            onClick={() => setFixada((v) => !v)}
            aria-expanded={aberta}
            aria-label={fixada ? 'Fechar menu' : 'Abrir menu'}
            className={cx(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors',
              escuro ? 'hover:bg-slate-800' : 'hover:bg-slate-100',
            )}
          >
            {/* A marca é o próprio botão: o logo tem fundo branco, então ganha
                uma pastilha para não virar um retângulo aceso no tema escuro. */}
            <img src="/ico.png" alt="Sistema TCM" className="h-8 w-8 rounded-lg bg-white object-contain p-0.5" />
          </button>

          <span
            className={cx(
              'flex-1 truncate text-sm font-bold whitespace-nowrap transition-opacity duration-200',
              aberta ? 'opacity-100' : 'pointer-events-none opacity-0',
              escuro ? 'text-slate-100' : 'text-slate-800',
            )}
          >
            Sistema TCM
          </span>

          {/* Fechar explícito: o véu já fecha no toque, mas num menu que cobre
              a tela inteira o operador procura o X. Só no celular — no desktop
              a barra se recolhe sozinha ao sair com o mouse. */}
          <button
            type="button"
            onClick={aoFecharGaveta}
            aria-label="Fechar menu"
            className={cx(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors lg:hidden',
              escuro ? 'text-slate-400 active:bg-slate-800' : 'text-slate-500 active:bg-slate-100',
            )}
          >
            <svg {...TRACO}>
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>

        <ul className="flex flex-1 flex-col gap-1 overflow-y-auto scrollbar-fina px-2 py-2">
        {itens.map((item) => {
          const Icone = item.icone;
          const ativo = item.para === destaque;

          return (
            <li key={item.para}>
              <Link
                to={item.para}
                title={aberta ? undefined : item.rotulo}
                // O `Link` marca `aria-current` por prefixo quando ativo, e em
                // `/caixa/novo` isso acendia "Caixa" e "Lançamento" ao mesmo
                // tempo. Restrito ao exato, quem decide é o `destaque`.
                activeOptions={{ exact: true }}
                aria-current={ativo ? 'page' : undefined}
                className={cx(
                  // `py-3` no dedo, `py-2.5` no mouse: 44 px é o alvo mínimo
                  // confortável de toque, e a lista tem dez itens seguidos.
                  'flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-colors lg:py-2.5',
                  ativo
                    ? escuro
                      ? 'bg-slate-800 text-brand-400'
                      : 'bg-brand-50 text-brand-700'
                    : escuro
                      ? 'text-slate-400 hover:bg-slate-800 hover:text-slate-100'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                )}
              >
                <span className="shrink-0">
                  <Icone />
                </span>
                <span
                  className={cx(
                    'truncate whitespace-nowrap transition-opacity duration-200',
                    aberta ? 'opacity-100' : 'pointer-events-none opacity-0',
                  )}
                >
                  {item.rotulo}
                </span>
              </Link>
            </li>
          );
        })}
        </ul>

        <Rodape aberta={aberta} escuro={escuro} />
      </nav>
    </>
  );
}

/**
 * Quem está usando o sistema, e o botão de sair.
 *
 * A guarda por `usuario` é só para o instante entre montar a casca e a
 * sessão chegar do cache — daí em diante sempre há alguém logado, porque
 * sem sessão nenhuma tela é montada.
 */
function Rodape({ aberta, escuro }: { aberta: boolean; escuro: boolean }) {
  const navegar = useNavigate();
  const sessao = useSessao();
  const sair = useSair();

  const usuario = sessao.data?.usuario;
  if (!usuario) return null;

  return (
    <div className={cx('shrink-0 border-t p-2', escuro ? 'border-slate-800' : 'border-slate-200')}>
      <button
        type="button"
        title={aberta ? undefined : `Sair (${usuario.nome})`}
        onClick={() =>
          sair.mutate(undefined, { onSuccess: () => void navegar({ to: '/login', replace: true }) })
        }
        className={cx(
          'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
          escuro
            ? 'text-slate-400 hover:bg-slate-800 hover:text-slate-100'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
        )}
      >
        <span className="shrink-0">
          <IconeSair />
        </span>
        <span
          className={cx(
            'min-w-0 flex-1 text-left transition-opacity duration-200',
            aberta ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
        >
          <span className="block truncate">{usuario.nome}</span>
          <span className="block truncate text-xs opacity-60">
            {usuario.papel} · sair
          </span>
        </span>
      </button>
    </div>
  );
}

function IconeSair() {
  return (
    <svg {...TRACO}>
      <path d="M15 17v1.5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2V7" />
      <path d="M10 12h10m0 0-3-3m3 3-3 3" />
    </svg>
  );
}

const TRACO = {
  className: 'h-5 w-5',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

function IconeMonitor() {
  return (
    <svg {...TRACO}>
      <rect x="3" y="4" width="7" height="7" rx="1.5" />
      <rect x="14" y="4" width="7" height="7" rx="1.5" />
      <rect x="3" y="15" width="7" height="5" rx="1.5" />
      <rect x="14" y="15" width="7" height="5" rx="1.5" />
    </svg>
  );
}

function IconeNovaOs() {
  return (
    <svg {...TRACO}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" />
      <path d="M14 3v5h5M12 11v6M9 14h6" />
    </svg>
  );
}

function IconeClientes() {
  return (
    <svg {...TRACO}>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 19a6 6 0 0 1 12 0M16 11.2A3.2 3.2 0 0 0 16 5M21 19a5 5 0 0 0-3.5-4.8" />
    </svg>
  );
}

function IconeCaixa() {
  return (
    <svg {...TRACO}>
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M3 10h18M17 15h.01" />
    </svg>
  );
}

function IconeLancamento() {
  return (
    <svg {...TRACO}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v8M8 12h8" />
    </svg>
  );
}

/** Nota fiscal — o mesmo ícone de "documento com valores" do `orc.html`. */
function IconeOrcamento() {
  return (
    <svg {...TRACO}>
      <path d="M6 3h12v18l-3-2-3 2-3-2-3 2V3Z" />
      <path d="M9 8h6M9 12h6M9 16h3" />
    </svg>
  );
}

function IconeMensagens() {
  return (
    <svg {...TRACO}>
      <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.4A8 8 0 1 1 21 12Z" />
    </svg>
  );
}

function IconeDashboard() {
  return (
    <svg {...TRACO}>
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
    </svg>
  );
}

function IconeAdmin() {
  return (
    <svg {...TRACO}>
      <rect x="2.5" y="4" width="7" height="5" rx="1.5" />
      <rect x="14.5" y="4" width="7" height="5" rx="1.5" />
      <rect x="14.5" y="15" width="7" height="5" rx="1.5" />
      <path d="M9.5 6.5h5M6 9v8.5h8.5" />
    </svg>
  );
}

function IconeZeradas() {
  return (
    <svg {...TRACO}>
      <path d="M12 4 2.5 20h19L12 4Z" />
      <path d="M12 10v4M12 17h.01" />
    </svg>
  );
}

export function Cabecalho({
  titulo,
  subtitulo,
  acao,
  escuro = false,
}: {
  titulo: string;
  subtitulo?: string;
  acao?: ReactNode;
  /** Acompanha o `escuro` do `AppShell` — sem isto o título some no fundo escuro. */
  escuro?: boolean;
}) {
  return (
    // No celular a ação desce para a própria linha em vez de disputar a
    // largura com o título. `items-start` e não `items-stretch`: assim um
    // `<Link>` em volta de um botão não vira uma faixa clicável do tamanho da
    // tela — quem quer ocupar a linha inteira pede `w-full` explicitamente.
    <header className="mb-5 flex flex-col items-start gap-3 sm:mb-6 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between sm:gap-4">
      <div className="min-w-0">
        <h1
          className={cx(
            // Um título de duas linhas custa caro numa tela de 375 px: o
            // `text-xl` mantém "Monitor de Ordens de Serviço" em uma só.
            'text-xl font-bold sm:text-2xl lg:text-3xl',
            escuro ? 'text-slate-100' : 'text-slate-800',
          )}
        >
          {titulo}
        </h1>
        {subtitulo && (
          <p className={cx('mt-1 text-sm', escuro ? 'text-slate-400' : 'text-slate-500')}>{subtitulo}</p>
        )}
      </div>
      {acao}
    </header>
  );
}

/**
 * Tradução de erro para linguagem de operador.
 *
 * O sistema atual mostra "Servidor desconectado, checar com Tech" para
 * qualquer falha — inclusive para "OS não encontrada". Aqui indisponibilidade
 * e erro de negócio são coisas diferentes, e o `requestId` aparece para o
 * suporte achar a ocorrência no log.
 */
export function ErroNaTela({ erro, aoTentar }: { erro: unknown; aoTentar?: () => void }) {
  if (!(erro instanceof ApiError)) {
    return <Faixa tipo="erro">Ocorreu um erro inesperado. Tente novamente.</Faixa>;
  }

  if (erro.ehIndisponibilidade) {
    return (
      <Faixa tipo="erro">
        <strong className="block">Servidor indisponível.</strong>
        Não foi possível falar com o banco de dados. Chame o suporte técnico.
        {aoTentar && (
          <button type="button" onClick={aoTentar} className="ml-2 underline font-semibold">
            Tentar de novo
          </button>
        )}
      </Faixa>
    );
  }

  return (
    <Faixa tipo="erro">
      <strong className="block">{erro.message}</strong>
      {erro.details.length > 0 && (
        <ul className="mt-1 list-disc pl-5">
          {erro.details.map((d, i) => (
            <li key={i}>
              {d.campo ? `${d.campo}: ` : ''}
              {d.mensagem}
            </li>
          ))}
        </ul>
      )}
      {erro.requestId && (
        <span className="mt-1 block text-xs opacity-70">Código da ocorrência: {erro.requestId}</span>
      )}
    </Faixa>
  );
}
